import './CreateDialog.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import useToastMessage from '../../../../../hooks/useToastMessage';
import { sequential } from '../../../../../utils/helpers/functions';
import DialogSpinner from '../../../../Dialogs/DialogSpinner/DialogSpinner';
import { LogisticsRoute } from '../../LogisticsRoutes.functions';
import { CreateLogisticsPoint } from './CreateDialog.functions';
import FormContainer from './FormContainer';

type Props = {
  isShown: boolean;
  data: any;
  isEditDialog: boolean;
  onHide: () => void;
  reloadCollection: () => void;
};

function CreateDialog({
  isShown,
  data,
  isEditDialog,
  onHide,
  reloadCollection,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const { data: routeData, isLoading: isRouteDataLoading } =
    useAxiosHook<LogisticsRoute>(`/logistics-route/${data?.id}`, {
      skipWhen: !isEditDialog || !data?.id,
    });

  const {
    data: createData,
    error: createError,
    isLoading: isCreateLoading,
    reload: createReload,
  } = useAxiosHook(
    {
      url: '/logistics-route',
      method: 'POST',
    },
    {
      skipWhen: true,
    }
  );

  useToastMessage(createData, createError, {
    success: {
      summary: t('The logistic route has been created successfully.'),
      callback: () => sequential(onHide, reloadCollection),
    },
    error: {
      summary: t('An error occurred while creating the logistic route.'),
      callback: () => {
        console.error('createError ', createError);
        if (createError?.response?.status === 409) {
          return t('The logistic route already exists.');
        }
        return t('An error occurred while creating the logistic route.');
      },
    },
  });

  const {
    data: editData,
    error: editError,
    isLoading: isEditLoading,
    reload: editReload,
  } = useAxiosHook(
    {
      url: `/logistics-route/${data?.id}`,
      method: 'PUT',
    },
    {
      skipWhen: true,
    }
  );

  useToastMessage(editData, editError, {
    success: {
      summary: t('The logistic route has been edited successfully.'),
      callback: () => sequential(onHide, reloadCollection),
    },
    error: {
      summary: t('An error occurred while editing the logistic route.'),
      callback: onHide,
    },
  });

  const dialogFooter = isRouteDataLoading ? null : (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        className="p-button-text"
        onClick={() => onHide()}
      />
      <Button
        type="submit"
        form="create-edit-logistics-route"
        label={
          isCreateLoading || isEditLoading
            ? t('Saving...')
            : isEditDialog
            ? t('Save changes')
            : t('Add route')
        }
        disabled={isCreateLoading || isEditLoading}
      />
    </>
  );

  function handleCreation(data: CreateLogisticsPoint) {
    createReload({ data });
  }

  function handleEditing(data: CreateLogisticsPoint) {
    editReload({ data });
  }

  return (
    <Dialog
      header={isEditDialog ? t('Edit route') : t('Create route')}
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 680, maxWidth: '100%' }}
      className="create-logistics-routes-page-dialog"
    >
      {isRouteDataLoading ? (
        <DialogSpinner />
      ) : (
        <FormContainer
          isEditDialog={isEditDialog}
          logisticRouteData={routeData}
          handleCreation={handleCreation}
          handleEditing={handleEditing}
        />
      )}
    </Dialog>
  );
}

export default CreateDialog;
